import { el, clear, setClass } from './dom';
import { icon } from './Icons';
import { compareSlider } from './EnhancePanel';
import type { ApiKeyStore } from '@/capture/ApiKeyStore';
import { CaptureUnsupportedError } from '@/capture/CapturePasses';
import type { CaptureResult, CaptureService } from '@/capture/CaptureService';
import { ENHANCE_MODEL, EnhanceError, enhanceRender } from '@/capture/EnhanceClient';
import { buildEnhancePrompt } from '@/capture/PromptBuilder';
import { collectRenderReferences } from '@/capture/RenderReferences';
import type { PhotoStorage, StoredShot } from '@/capture/PhotoStorage';
import type { SemanticTag } from '@/semantics/SemanticTag';
import { tagImageToBlob } from './ImageIntake';
import {
  DEFAULT_STYLE_PRESET_ID,
  STYLE_PRESETS,
  getStylePreset,
  type StylePreset,
} from '@/capture/StylePresets';
import {
  RENDER_QUALITY_TIERS,
  SIZE_PRESETS,
  estimateCostUsd,
  formatCostUsd,
  type ImageSize,
  type RenderQualityTier,
} from '@/capture/ImageSpec';

/**
 * One-click AI render of the frame the user is already looking at.
 *
 * Photo mode is the considered path: enter it, compose against a letterbox, take a shot, open the
 * gallery, open the enhance panel, pick a style, render. That sequence exists because a shot is a
 * durable artefact worth composing. But most of the time the question is simply "what would *this*
 * look like rendered", and answering it should not cost six clicks and a mode change.
 *
 * So this widget is the same pipeline with the deliberation removed: capture the live viewport,
 * build the prompt from the manifest, send it, show the result. The settings that photo mode spreads
 * across a panel live in one popover behind the caret, are remembered between sessions, and are
 * never asked for again — the button renders with whatever was chosen last.
 *
 * It owns no domain state. Captures go through {@link CaptureService} exactly as photo mode's
 * shutter does, and a kept render is filed in the same {@link PhotoStorage} the gallery reads, so a
 * quick render is indistinguishable from a composed one once it lands.
 */

export interface QuickRenderDeps {
  capture: CaptureService;
  /** The credential the render runs on. Shared with photo mode and the rest of the AI features. */
  apiKeys: ApiKeyStore;
  /**
   * Where a kept render is filed, resolved per call rather than held.
   *
   * The app swaps this store when a user signs in or out, so a getter means a quick render taken
   * after a sign-in lands in the account rather than in the store that existed at construction.
   * Returning null simply removes the Keep action — the render is still downloadable.
   */
  store?: () => PhotoStorage | null;
  /** Called after a render is filed, so the gallery can pick it up. */
  onFiled?: (shot: StoredShot) => void;
  /**
   * The site's semantic tags, resolved per call.
   *
   * Read for the reference photographs attached to them: a tag carrying a picture of the fence is
   * how that fence renders correctly, and it has to happen on every render rather than only when
   * somebody remembers to re-upload the picture. Absent, the feature is simply off.
   */
  tags?: () => readonly SemanticTag[];
  /**
   * The site's name, resolved per call, used to name downloads.
   *
   * A getter for the same reason `store` is one: the name changes with every save, rename, and open,
   * and a widget built at startup would otherwise stamp every export for the rest of the session
   * with whatever the site was called before any of that happened.
   */
  siteName?: () => string;
}

/** A follow-up render: what the user typed and uploaded in the "Render again" dialog. */
export interface QuickRenderFollowUp {
  /** Replaces the saved art direction for this render only. */
  direction: string;
  /** Designer-supplied reference photographs, sent after the three captured passes. */
  references: readonly Blob[];
}

/** `'viewport'` follows the window; the rest name a documented output size. */
export type FramingId = string;

export interface QuickRenderSettings {
  presetId: string;
  quality: RenderQualityTier;
  framing: FramingId;
  variations: number;
  direction: string;
  /** File every successful render in Shots without being asked. */
  autoFile: boolean;
}

export const QUICK_RENDER_STORAGE_KEY = 'landscape-glade.quick-render';
export const VARIATION_CHOICES = [1, 2, 4] as const;
/** Longest art direction the popover will keep. Beyond this it is prose, not direction. */
export const MAX_DIRECTION_LENGTH = 400;

/**
 * Reference photographs a follow-up render may carry.
 *
 * Capped at four because each one is a full-price image input on a request the user pays for, and
 * because a mood board past four images stops steering a render and starts diluting it. The type
 * list is the endpoint's, not a guess — anything else comes back as a 400 after the capture has
 * already been made and paid for.
 */
export const MAX_REFERENCE_IMAGES = 4;
export const MAX_REFERENCE_BYTES = 12 * 1024 * 1024;
export const REFERENCE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** The parts of a `File` the intake check actually reads, so it can be tested without a DOM. */
export interface ReferenceCandidate {
  name: string;
  type: string;
  size: number;
}

export interface ReferenceIntake<T> {
  accepted: T[];
  /** One sentence per refusal, already worded for display. */
  rejected: string[];
}

/**
 * Decide which picked files may join the reference set.
 *
 * Every rejection is reported rather than silently dropped. A file the user chose and cannot see in
 * the strip afterwards reads as a bug in the picker, and the one thing worse than refusing a 40 MB
 * TIFF is refusing it without saying so and then rendering without it.
 */
export function acceptReferenceFiles<T extends ReferenceCandidate>(
  held: readonly T[],
  incoming: readonly T[],
): ReferenceIntake<T> {
  const accepted: T[] = [];
  const rejected: string[] = [];
  const seen = new Set(held.map((file) => `${file.name}:${file.size}`));

  for (const file of incoming) {
    const key = `${file.name}:${file.size}`;
    if (seen.has(key)) {
      rejected.push(`${file.name} — already added.`);
      continue;
    }
    if (held.length + accepted.length >= MAX_REFERENCE_IMAGES) {
      rejected.push(`${file.name} — at most ${MAX_REFERENCE_IMAGES} reference photos.`);
      continue;
    }
    if (!(REFERENCE_MIME_TYPES as readonly string[]).includes(file.type)) {
      rejected.push(`${file.name} — PNG, JPEG, or WebP only.`);
      continue;
    }
    if (file.size > MAX_REFERENCE_BYTES) {
      rejected.push(
        `${file.name} — larger than ${Math.round(MAX_REFERENCE_BYTES / (1024 * 1024))} MB.`,
      );
      continue;
    }
    seen.add(key);
    accepted.push(file);
  }

  return { accepted, rejected };
}

export interface RenderFileNameParts {
  /** The site's name as the user typed it. Slugged here, not by the caller. */
  site: string;
  /** `YYYYMMDD-HHMMSS`, taken once per render so a whole batch shares it. */
  stamp: string;
  presetId: string;
  /** `'ai'` for the model's output, `'model'` for the captured view it was made from. */
  kind: 'ai' | 'model';
  /** 1-based, and only supplied when one render produced several variations. */
  variation?: number;
}

/**
 * The name a quick render is saved under.
 *
 * Two constraints shape it. The pair — the AI render and the model view it came from — has to land
 * side by side in a downloads folder, so both are the same name up to a final `-ai` / `-model`, and
 * a shared timestamp keeps a batch of variations together. And exports from two different sites must
 * never overwrite each other, so the site's own name leads: `quick-render-photoreal-142233.png` was
 * the same filename for every project on the machine, which is precisely the collision to avoid.
 *
 * Everything outside `[a-z0-9]` is folded to a dash, because these names cross Windows, macOS, and
 * whatever the browser does to a `download` attribute it dislikes.
 */
export function quickRenderFileName(parts: RenderFileNameParts): string {
  const segments = [
    fileSlug(parts.site, 48) || 'site',
    parts.stamp,
    fileSlug(parts.presetId, 24) || 'render',
  ];
  if (parts.variation !== undefined) segments.push(`v${parts.variation}`);
  segments.push(parts.kind);
  return `${segments.join('-')}.png`;
}

function fileSlug(value: string, max: number): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, max)
    .replace(/-+$/, '');
}

export function defaultQuickRenderSettings(): QuickRenderSettings {
  return {
    presetId: DEFAULT_STYLE_PRESET_ID,
    quality: 'medium',
    framing: 'viewport',
    variations: 1,
    direction: '',
    autoFile: true,
  };
}

/**
 * Parse remembered settings, treating anything unrecognised as absent.
 *
 * Every field is checked against what this build actually ships rather than trusted for its type. A
 * style preset that has since been retired, or a quality tier written by a newer build, would
 * otherwise ride straight into the request — and an unknown `quality` comes back as an opaque 400
 * *after* the user has waited for a capture. Falling back to the default is always recoverable; the
 * popover is one click away and shows what is in force.
 */
export function parseQuickRenderSettings(raw: string | null | undefined): QuickRenderSettings {
  const base = defaultQuickRenderSettings();
  if (!raw) return base;
  let parsed: Partial<QuickRenderSettings>;
  try {
    parsed = JSON.parse(raw) as Partial<QuickRenderSettings>;
  } catch {
    return base;
  }
  if (parsed === null || typeof parsed !== 'object') return base;

  const framingKnown =
    parsed.framing === 'viewport' || SIZE_PRESETS.some((preset) => preset.id === parsed.framing);

  return {
    presetId: STYLE_PRESETS.some((preset) => preset.id === parsed.presetId)
      ? (parsed.presetId as string)
      : base.presetId,
    quality: RENDER_QUALITY_TIERS.includes(parsed.quality as RenderQualityTier)
      ? (parsed.quality as RenderQualityTier)
      : base.quality,
    framing: framingKnown ? (parsed.framing as string) : base.framing,
    variations: VARIATION_CHOICES.includes(parsed.variations as 1 | 2 | 4)
      ? (parsed.variations as number)
      : base.variations,
    direction:
      typeof parsed.direction === 'string' ? parsed.direction.slice(0, MAX_DIRECTION_LENGTH) : '',
    autoFile: typeof parsed.autoFile === 'boolean' ? parsed.autoFile : base.autoFile,
  };
}

/**
 * The output size a framing choice asks for.
 *
 * `'viewport'` defers to the capture service, which snaps the window's shape to the nearest
 * documented size rather than deriving an exact one — an unlisted size is legal by every published
 * rule and still a 400. Anything else names a size from that same table, so neither branch can
 * produce something the endpoint has not guaranteed.
 */
export function resolveRenderSize(framing: FramingId, suggested: () => ImageSize): ImageSize {
  if (framing === 'viewport') return suggested();
  const preset = SIZE_PRESETS.find((entry) => entry.id === framing);
  return preset?.size ?? (SIZE_PRESETS[0] as { size: ImageSize }).size;
}

function readSettings(): QuickRenderSettings {
  try {
    return parseQuickRenderSettings(globalThis.localStorage?.getItem(QUICK_RENDER_STORAGE_KEY));
  } catch {
    return defaultQuickRenderSettings();
  }
}

export class QuickRender {
  /** Mounted over the canvas column; the bar pins itself to the top-right corner of it. */
  readonly root: HTMLElement;

  private settings = readSettings();
  private running = false;
  private controller: AbortController | null = null;
  /** The prompt the visible result was made from, shown under it and stored with a kept render. */
  private lastPrompt = '';
  private filedShotId: string | null = null;
  private shotCounter = 0;
  /** Object URLs minted for result previews, revoked when the sheet is cleared. */
  private readonly objectUrls: string[] = [];
  /** Taken once per render, so every file a batch saves carries the same instant. */
  private lastStamp = fileStamp();
  /**
   * What the "Render again" dialog last held.
   *
   * Kept across renders rather than cleared with the sheet, because iterating is the whole point of
   * the dialog: the second pass almost always starts from the note and the photographs that produced
   * the first, with one line changed. `null` means it has never been opened, in which case it seeds
   * itself from the saved art direction instead.
   */
  private followUpDirection: string | null = null;
  private readonly references: { file: File; url: string }[] = [];

  /**
   * The placeholder standing in for the render while it is being made.
   *
   * Held rather than rebuilt because the phase line and the clock inside it are written to as the
   * render moves through capture and generation. `null` whenever nothing is in flight.
   */
  private working: HTMLElement | null = null;
  private workingPhase: HTMLElement | null = null;
  private workingClock: HTMLElement | null = null;
  private workingTimer: ReturnType<typeof setInterval> | null = null;

  private readonly bar: HTMLElement;
  /** The top-right corner row: anything mounted alongside the bar, then the bar itself. */
  private readonly corner: HTMLElement;
  private readonly goButton: HTMLButtonElement;
  private readonly caret: HTMLButtonElement;
  private readonly pop: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly sheetBody: HTMLElement;
  private readonly styleRow: HTMLElement;
  private readonly qualityRow: HTMLElement;
  private readonly framingRow: HTMLElement;
  private readonly variationRow: HTMLElement;
  private readonly directionField: HTMLInputElement;
  private readonly autoFileBox: HTMLInputElement;
  private readonly costLine: HTMLElement;
  private readonly keyNote: HTMLElement;
  private readonly statusLine: HTMLElement;
  private readonly ask: HTMLElement;
  private readonly askField: HTMLTextAreaElement;
  private readonly askFiles: HTMLInputElement;
  private readonly askStrip: HTMLElement;
  private readonly askNote: HTMLElement;
  private readonly unsubscribe: (() => void)[] = [];

  /** Closes the popover on a click anywhere else. Bound once, attached only while it is open. */
  private readonly onDocumentPointerDown = (event: PointerEvent): void => {
    if (event.target instanceof Node && this.bar.contains(event.target)) return;
    if (event.target instanceof Node && this.pop.contains(event.target)) return;
    this.setPopoverOpen(false);
  };

  constructor(private readonly deps: QuickRenderDeps) {
    this.styleRow = el('div', { class: 'lg-quick__styles' });
    this.qualityRow = el('div', { class: 'lg-seg' });
    this.framingRow = el('div', { class: 'lg-seg' });
    this.variationRow = el('div', { class: 'lg-seg' });
    this.costLine = el('p', { class: 'lg-quick__cost' });
    this.keyNote = el('p', { class: 'lg-quick__note' });
    this.statusLine = el('p', { class: 'lg-quick__status' });

    this.directionField = el('input', {
      class: 'lg-quick__input',
      attrs: {
        type: 'text',
        placeholder: 'Optional — e.g. "add a flagstone path"',
        'aria-label': 'Additional art direction',
        maxlength: String(MAX_DIRECTION_LENGTH),
      },
      on: {
        input: () => {
          this.settings.direction = this.directionField.value;
          this.commit();
        },
      },
    });

    this.autoFileBox = el('input', {
      class: 'lg-quick__check',
      attrs: { type: 'checkbox', id: 'lg-quick-autofile' },
      on: {
        change: () => {
          this.settings.autoFile = this.autoFileBox.checked;
          this.commit();
        },
      },
    });

    this.goButton = el('button', {
      class: 'lg-quick__go',
      attrs: { type: 'button' },
      html: `${icon('sparkle', 16)}<span class="lg-quick__label">Render</span>`,
      on: { click: () => this.handleGo() },
    });

    this.caret = el('button', {
      class: 'lg-quick__caret',
      attrs: { type: 'button', 'aria-label': 'Render settings', 'aria-expanded': 'false' },
      title: 'Render settings',
      html: icon('chevron', 14),
      on: { click: () => this.setPopoverOpen(!this.isPopoverOpen) },
    });

    this.bar = el(
      'div',
      { class: 'lg-quick__bar', attrs: { 'aria-label': 'AI render' } },
      this.goButton,
      this.caret,
    );

    // The bar no longer pins itself to the corner — this row does, and holds whatever else belongs
    // beside it. The popover still hangs off the *overlay's* right edge, which is the bar's edge
    // however many controls end up to its left.
    this.corner = el('div', { class: 'lg-quick__corner' }, this.bar);

    this.askField = el('textarea', {
      class: 'lg-quick__textarea',
      attrs: {
        rows: '3',
        placeholder: 'e.g. "warmer evening light, and swap the gravel for sawn bluestone"',
        'aria-label': 'Art direction for this render',
        maxlength: String(MAX_DIRECTION_LENGTH),
      },
    });

    this.askFiles = el('input', {
      class: 'lg-quick__file',
      attrs: {
        type: 'file',
        accept: REFERENCE_MIME_TYPES.join(','),
        multiple: true,
        'aria-label': 'Add reference photos',
      },
      on: {
        change: () => {
          this.addReferences(Array.from(this.askFiles.files ?? []));
          // Cleared so picking the same file again after removing it still fires a change event.
          this.askFiles.value = '';
        },
      },
    });

    this.askStrip = el('div', { class: 'lg-quick__strip' });
    this.askNote = el('p', { class: 'lg-quick__asknote', attrs: { hidden: true } });

    this.pop = this.buildPopover();
    this.sheetBody = el('div', { class: 'lg-quick__sheetbody' });
    this.sheet = this.buildSheet();
    this.ask = this.buildAsk();

    this.root = el('div', { class: 'lg-quick' }, this.corner, this.pop, this.sheet, this.ask);

    // The editor listens for bare keys on `window`; it already ignores fields, but Escape has to be
    // claimed here or closing the popover would also clear the selection behind it.
    this.root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      if (this.isAskOpen) this.closeAsk();
      else if (this.isSheetOpen) this.closeSheet();
      else if (this.isPopoverOpen) this.setPopoverOpen(false);
    });

    this.buildStyleRow();
    this.buildQualityRow();
    this.buildFramingRow();
    this.buildVariationRow();

    this.directionField.value = this.settings.direction;
    this.autoFileBox.checked = this.settings.autoFile;

    // The button is dead without a credential, and signing in is what supplies one — so the widget
    // has to hear about it rather than waiting for the next render attempt to discover it.
    this.unsubscribe.push(this.deps.apiKeys.onChange(() => this.refresh()));

    this.refresh();
  }

  private get isPopoverOpen(): boolean {
    return this.pop.hidden === false;
  }

  private get isSheetOpen(): boolean {
    return this.sheet.hidden === false;
  }

  private get isAskOpen(): boolean {
    return this.ask.hidden === false;
  }

  /**
   * Mount a control immediately left of the render bar, in the same corner row.
   *
   * For the things that decide what a render will *be* before the button is pressed — the lens, so
   * far. They live here rather than in a corner of their own so the two read as one cluster, and so
   * they go away with the render bar when photo mode clears the frame.
   */
  mountLeading(element: HTMLElement): void {
    this.corner.insertBefore(element, this.bar);
  }

  /* ---------------------------------- Construction ---------------------------------- */

  private buildPopover(): HTMLElement {
    return el(
      'div',
      {
        class: 'lg-quick__pop',
        attrs: { role: 'dialog', 'aria-label': 'AI render settings', hidden: true },
      },
      el(
        'header',
        { class: 'lg-quick__pophead' },
        el('h2', { class: 'lg-quick__poptitle', text: 'AI render' }),
        el('button', {
          class: 'lg-quick__popclose',
          html: icon('close', 14),
          title: 'Close',
          attrs: { type: 'button', 'aria-label': 'Close render settings' },
          on: { click: () => this.setPopoverOpen(false) },
        }),
      ),
      this.keyNote,
      field('Style', this.styleRow),
      field('Quality', this.qualityRow),
      field('Framing', this.framingRow),
      field('Variations', this.variationRow),
      field('Art direction', this.directionField),
      el(
        'label',
        { class: 'lg-quick__row' },
        this.autoFileBox,
        el('span', { text: 'File every render in Shots' }),
      ),
      this.costLine,
    );
  }

  private buildSheet(): HTMLElement {
    return el(
      'div',
      { class: 'lg-quick__sheet', attrs: { role: 'dialog', 'aria-label': 'Render result', hidden: true } },
      el(
        'header',
        { class: 'lg-quick__sheethead' },
        el('span', { class: 'lg-quick__sheettitle', text: 'AI render' }),
        this.statusLine,
        el('button', {
          class: 'lg-quick__popclose',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the render' },
          on: { click: () => this.closeSheet() },
        }),
      ),
      this.sheetBody,
    );
  }

  /**
   * The dialog behind "Render again".
   *
   * Rendering again used to mean re-rolling the same request and hoping for a better draw, which is
   * the least useful thing a second render can be. What a designer actually has after looking at a
   * result is a correction — "the paving is too orange", "make it read like this photograph" — and
   * this is where that correction goes. The note replaces the saved art direction for one render
   * rather than editing it, so a one-off instruction does not silently become permanent, and the
   * photographs ride along as extra inputs the prompt numbers explicitly.
   */
  private buildAsk(): HTMLElement {
    const card = el(
      'div',
      { class: 'lg-quick__askcard', attrs: { role: 'dialog', 'aria-label': 'Render again' } },
      el(
        'header',
        { class: 'lg-quick__pophead' },
        el('h2', { class: 'lg-quick__poptitle', text: 'Render again' }),
        el('button', {
          class: 'lg-quick__popclose',
          html: icon('close', 14),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Cancel' },
          on: { click: () => this.closeAsk() },
        }),
      ),
      el('p', {
        class: 'lg-quick__askblurb',
        text: 'Recaptures this view and renders it again. Add a note or reference photos to steer the result.',
      }),
      field('What to change', this.askField),
      field(
        'Reference photos',
        el(
          'label',
          { class: 'lg-quick__filerow' },
          this.askFiles,
          el('span', {
            class: 'lg-btn lg-btn--ghost lg-quick__filebtn',
            html: `${icon('upload', 15)}<span>Add photos</span>`,
          }),
        ),
        this.askStrip,
      ),
      this.askNote,
      el(
        'div',
        { class: 'lg-quick__askactions' },
        el('button', {
          class: 'lg-btn lg-btn--ghost',
          attrs: { type: 'button' },
          text: 'Cancel',
          on: { click: () => this.closeAsk() },
        }),
        el('button', {
          class: 'lg-btn lg-btn--primary',
          attrs: { type: 'button' },
          html: `${icon('sparkle', 15)}<span>Render</span>`,
          on: { click: () => this.submitAsk() },
        }),
      ),
    );

    // Ctrl/Cmd+Enter submits: the field is a textarea because corrections run to a sentence or two,
    // and a plain Enter has to keep inserting a newline.
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.submitAsk();
      }
    });

    return el(
      'div',
      { class: 'lg-quick__ask', attrs: { hidden: true } },
      // The scrim only dismisses; it does not submit. A click outside a dialog that would spend money
      // has to mean "not that".
      el('div', {
        class: 'lg-quick__askscrim',
        on: { click: () => this.closeAsk() },
      }),
      card,
    );
  }

  private buildStyleRow(): void {
    for (const preset of STYLE_PRESETS) {
      this.styleRow.appendChild(
        el('button', {
          class: 'lg-quick__style',
          text: preset.label,
          title: preset.blurb,
          attrs: { type: 'button', 'data-preset': preset.id },
          on: {
            click: () => {
              this.settings.presetId = preset.id;
              this.commit();
            },
          },
        }),
      );
    }
  }

  private buildQualityRow(): void {
    for (const tier of RENDER_QUALITY_TIERS) {
      this.qualityRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: tier,
          attrs: { type: 'button', 'data-quality': tier },
          title:
            tier === 'low'
              ? 'Fastest and cheapest — good for checking an idea'
              : tier === 'medium'
                ? 'Balanced quality and cost'
                : 'Best detail, highest cost',
          on: {
            click: () => {
              this.settings.quality = tier;
              this.commit();
            },
          },
        }),
      );
    }
  }

  private buildFramingRow(): void {
    const options: { id: FramingId; label: string; title: string }[] = [
      {
        id: 'viewport',
        label: 'Fit',
        title: 'The supported output size closest to this window’s shape',
      },
      ...SIZE_PRESETS.map((preset) => ({
        id: preset.id,
        label: preset.label.split('·')[0]?.trim() ?? preset.id,
        title: preset.label,
      })),
    ];

    for (const option of options) {
      this.framingRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: option.label,
          title: option.title,
          attrs: { type: 'button', 'data-framing': option.id },
          on: {
            click: () => {
              this.settings.framing = option.id;
              this.commit();
            },
          },
        }),
      );
    }
  }

  private buildVariationRow(): void {
    for (const count of VARIATION_CHOICES) {
      this.variationRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: String(count),
          attrs: { type: 'button', 'data-variations': String(count) },
          title: `Generate ${count} variation${count > 1 ? 's' : ''}`,
          on: {
            click: () => {
              this.settings.variations = count;
              this.commit();
            },
          },
        }),
      );
    }
  }

  /* ------------------------------------- State ------------------------------------- */

  /** The output size this render will ask for. */
  renderSize(): ImageSize {
    return resolveRenderSize(this.settings.framing, () => this.deps.capture.suggestedSize());
  }

  private preset(): StylePreset {
    return getStylePreset(this.settings.presetId);
  }

  private commit(): void {
    try {
      globalThis.localStorage?.setItem(QUICK_RENDER_STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Non-fatal: the settings simply do not survive this session.
    }
    this.refresh();
  }

  private setPopoverOpen(open: boolean): void {
    this.pop.hidden = !open;
    this.caret.setAttribute('aria-expanded', String(open));
    setClass(this.caret, 'is-open', open);
    if (open) {
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      this.refresh();
    } else {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }
  }

  private refresh(): void {
    for (const button of this.styleRow.querySelectorAll<HTMLElement>('[data-preset]')) {
      setClass(button, 'is-active', button.dataset.preset === this.settings.presetId);
    }
    for (const button of this.qualityRow.querySelectorAll<HTMLElement>('[data-quality]')) {
      setClass(button, 'is-active', button.dataset.quality === this.settings.quality);
    }
    for (const button of this.framingRow.querySelectorAll<HTMLElement>('[data-framing]')) {
      setClass(button, 'is-active', button.dataset.framing === this.settings.framing);
    }
    for (const button of this.variationRow.querySelectorAll<HTMLElement>('[data-variations]')) {
      setClass(button, 'is-active', button.dataset.variations === String(this.settings.variations));
    }

    const hasKey = this.deps.apiKeys.has();
    const supported = this.deps.capture.supported;
    const size = this.renderSize();
    const cost = estimateCostUsd(size, this.settings.quality, this.settings.variations);

    this.costLine.textContent = `${ENHANCE_MODEL} · ${size.width}×${size.height} · about ${formatCostUsd(cost)}`;

    this.keyNote.textContent = hasKey
      ? ''
      : 'No API key yet. Add one in photo mode’s Enhance panel, or sign in, to enable rendering.';
    this.keyNote.hidden = hasKey;

    setClass(this.bar, 'is-busy', this.running);

    // The button stays live while running so it can cancel; only a missing credential or an
    // unsupported renderer actually disables it.
    this.goButton.disabled = !this.running && (!hasKey || !supported);
    const label = this.goButton.querySelector('.lg-quick__label');
    if (label) label.textContent = this.running ? 'Cancel' : 'Render';
    this.goButton.title = this.running
      ? 'Cancel this render'
      : !supported
        ? 'This browser session cannot capture frames.'
        : !hasKey
          ? 'An OpenAI API key is required to render.'
          : `Render this view — ${this.preset().label}, ${this.settings.quality}, ${size.width}×${size.height}, about ${formatCostUsd(cost)}`;
  }

  private setStatus(message: string, tone: 'ok' | 'error' | 'busy'): void {
    this.statusLine.textContent = message;
    this.statusLine.dataset.tone = tone;
    // The sheet carries the busy flag so the header pill can light up as a whole; a tone attribute on
    // the sentence alone only ever coloured the sentence.
    setClass(this.sheet, 'is-busy', tone === 'busy');
    if (tone === 'busy' && this.workingPhase) this.workingPhase.textContent = message;
  }

  /* ------------------------------------- Working ------------------------------------- */

  /**
   * Claim the space the render will land in, for as long as it takes to make it.
   *
   * A render costs money and the better part of a minute, and the only signs it was running were a
   * sparkle turning inside a 32px corner button and one sentence in a pill — both easy to miss, and
   * easy to mistake for nothing having happened. So the sheet puts a placeholder where the result
   * will be, in the render's own proportions, and counts the seconds out loud: the wait becomes a
   * visible object, cancelling is under the hand rather than back up in the corner, and nobody
   * presses the button a second time wondering whether the first one took.
   *
   * Small, though. It is a progress report, not a picture, and the space it used to fill belongs to
   * the render that lands in it (see `.lg-quick__result` in `quickrender.css`).
   */
  private showWorking(): void {
    this.stopWorking();
    const size = this.renderSize();

    this.workingPhase = el('p', { class: 'lg-quick__workphase', text: 'Capturing the view…' });
    this.workingClock = el('span', { class: 'lg-quick__workclock', text: '0s' });

    this.working = el(
      'div',
      { class: 'lg-quick__working' },
      el(
        'div',
        {
          class: 'lg-quick__workpane',
          // The pane is the render's own aspect ratio, so what is coming reads as a landscape or a
          // portrait from the first second rather than as a generic box.
          style: { aspectRatio: `${size.width} / ${size.height}` },
        },
        el('div', { class: 'lg-quick__worksheen' }),
        el('div', { class: 'lg-quick__workmark', html: icon('sparkle', 22) }),
      ),
      el(
        'div',
        { class: 'lg-quick__workfoot' },
        this.workingPhase,
        this.workingClock,
        el('button', {
          class: 'lg-btn lg-btn--ghost',
          attrs: { type: 'button' },
          text: 'Cancel',
          title: 'Stop this render',
          on: { click: () => this.controller?.abort() },
        }),
      ),
    );

    this.sheetBody.appendChild(this.working);

    const started = Date.now();
    this.workingTimer = setInterval(() => {
      const seconds = Math.round((Date.now() - started) / 1000);
      if (this.workingClock) this.workingClock.textContent = `${seconds}s`;
    }, 1000);
  }

  private stopWorking(): void {
    if (this.workingTimer !== null) clearInterval(this.workingTimer);
    this.workingTimer = null;
    this.working?.remove();
    this.working = null;
    this.workingPhase = null;
    this.workingClock = null;
  }

  /* --------------------------------- Render again --------------------------------- */

  private openAsk(): void {
    this.askField.value = this.followUpDirection ?? this.settings.direction;
    this.setAskNote([]);
    this.drawStrip();
    this.ask.hidden = false;
    this.askField.focus();
    this.askField.setSelectionRange(this.askField.value.length, this.askField.value.length);
  }

  private closeAsk(): void {
    this.ask.hidden = true;
  }

  /**
   * Take the dialog's contents and start the render.
   *
   * The note is remembered on the widget rather than written to settings: it describes this frame,
   * and a correction about one view's paving should not attach itself to every render made from then
   * on. Reopening the dialog still shows it, which is the part that makes iterating cheap.
   */
  private submitAsk(): void {
    const direction = this.askField.value.trim().slice(0, MAX_DIRECTION_LENGTH);
    this.followUpDirection = direction;
    this.closeAsk();
    void this.run({ direction, references: this.references.map((entry) => entry.file) });
  }

  private addReferences(files: readonly File[]): void {
    const { accepted, rejected } = acceptReferenceFiles(
      this.references.map((entry) => entry.file),
      files,
    );
    for (const file of accepted) {
      this.references.push({ file, url: URL.createObjectURL(file) });
    }
    this.setAskNote(rejected);
    this.drawStrip();
  }

  private removeReference(index: number): void {
    const [entry] = this.references.splice(index, 1);
    if (entry) URL.revokeObjectURL(entry.url);
    this.setAskNote([]);
    this.drawStrip();
  }

  private setAskNote(lines: readonly string[]): void {
    this.askNote.textContent = lines.join(' ');
    this.askNote.hidden = lines.length === 0;
  }

  private drawStrip(): void {
    clear(this.askStrip);
    this.askStrip.hidden = this.references.length === 0;
    this.references.forEach((entry, index) => {
      this.askStrip.appendChild(
        el(
          'div',
          { class: 'lg-quick__chip', title: entry.file.name },
          el('img', { attrs: { src: entry.url, alt: entry.file.name } }),
          el('button', {
            class: 'lg-quick__chipx',
            html: icon('close', 12),
            attrs: { type: 'button', 'aria-label': `Remove ${entry.file.name}` },
            on: { click: () => this.removeReference(index) },
          }),
        ),
      );
    });
  }

  /* -------------------------------------- Run -------------------------------------- */

  private handleGo(): void {
    if (this.running) {
      this.controller?.abort();
      return;
    }
    void this.run();
  }

  /**
   * Capture the live viewport and render it.
   *
   * The capture passes hide the editor's overlay geometry themselves, and the HUD is DOM rather than
   * scene content, so nothing has to be torn down first — which is the whole reason this can be one
   * click where photo mode is a mode.
   *
   * `followUp` carries what the "Render again" dialog collected. Absent, the render is exactly the
   * one the toolbar button has always made: the saved settings, no extra inputs.
   */
  async run(followUp?: QuickRenderFollowUp): Promise<void> {
    const apiKey = this.deps.apiKeys.get();
    if (this.running) return;
    if (!this.deps.capture.supported) {
      this.openSheet();
      this.setStatus('This browser session cannot capture frames.', 'error');
      return;
    }
    if (apiKey === null) {
      this.setPopoverOpen(true);
      return;
    }

    this.running = true;
    this.controller = new AbortController();
    this.setPopoverOpen(false);
    this.clearSheet();
    this.openSheet();
    this.showWorking();
    this.setStatus('Capturing the view…', 'busy');
    this.refresh();

    const preset = this.preset();
    const direction = (followUp?.direction ?? this.settings.direction).trim();
    const references = (followUp?.references ?? []).slice(0, MAX_REFERENCE_IMAGES);

    try {
      const capture = await this.deps.capture.capture(this.renderSize());
      this.filedShotId = null;
      this.lastStamp = fileStamp();

      // Tagged references are resolved against the frame that was just captured, so a picture only
      // travels when the thing it describes is actually visible. They take the slots immediately
      // after the passes, ahead of anything attached to this one render — see `PromptBuilder`.
      const tagged = collectRenderReferences(capture.manifest, this.deps.tags?.() ?? [], {
        reservedSlots: references.length,
      });
      const taggedBlobs = tagged.images
        .map((image) => tagImageToBlob(image))
        .filter((blob): blob is Blob => blob !== null);

      const { text } = buildEnhancePrompt(capture.manifest, preset, {
        ...(direction ? { extraDirection: direction } : {}),
        ...(tagged.references.length > 0 ? { references: tagged.references } : {}),
        ...(references.length > 0 ? { referenceCount: references.length } : {}),
      });
      this.lastPrompt = text;

      const plants = capture.manifest.visiblePlantCount;
      const attachedCount = references.length + taggedBlobs.length;
      const withRefs =
        attachedCount > 0
          ? `, ${attachedCount} reference photo${attachedCount === 1 ? '' : 's'}`
          : '';
      this.setStatus(
        `Rendering — ${plants} plant${plants === 1 ? '' : 's'} in frame${withRefs}…`,
        'busy',
      );

      const result = await enhanceRender({
        apiKey,
        images: [capture.beauty, capture.idMap, capture.depth, ...taggedBlobs, ...references],
        prompt: text,
        size: capture.size,
        quality: this.settings.quality,
        count: this.settings.variations,
        signal: this.controller.signal,
      });

      this.showResults(capture, result.images, preset);
      this.setStatus(
        `${result.images.length} render${result.images.length > 1 ? 's' : ''} · ${preset.label}`,
        'ok',
      );

      if (this.settings.autoFile) await this.file(capture, result.images, preset);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.setStatus('Render cancelled.', 'ok');
        this.closeSheet();
      } else if (error instanceof CaptureUnsupportedError || error instanceof EnhanceError) {
        this.setStatus(error.message, 'error');
      } else {
        this.setStatus(error instanceof Error ? error.message : 'The render failed.', 'error');
      }
    } finally {
      this.running = false;
      this.controller = null;
      this.stopWorking();
      this.refresh();
    }
  }

  /* ------------------------------------ Results ------------------------------------ */

  private showResults(capture: CaptureResult, images: Blob[], preset: StylePreset): void {
    this.stopWorking();
    clear(this.sheetBody);
    const beforeUrl = this.trackUrl(URL.createObjectURL(capture.beauty));
    const store = this.deps.store?.() ?? null;
    const site = this.deps.siteName?.() ?? '';

    images.forEach((image, index) => {
      // A variation number only appears when there is something to tell apart; a lone render is not
      // "v1 of 1", and saying so would put a number in every filename to serve the rare batch.
      const naming = {
        site,
        stamp: this.lastStamp,
        presetId: preset.id,
        ...(images.length > 1 ? { variation: index + 1 } : {}),
      };
      const renderName = quickRenderFileName({ ...naming, kind: 'ai' });
      const modelName = quickRenderFileName({ ...naming, kind: 'model' });

      const afterUrl = this.trackUrl(URL.createObjectURL(image));
      this.sheetBody.appendChild(
        el(
          'div',
          { class: 'lg-quick__result' },
          compareSlider(beforeUrl, afterUrl),
          el(
            'div',
            { class: 'lg-quick__actions' },
            store === null || this.settings.autoFile
              ? null
              : el('button', {
                  class: 'lg-btn lg-btn--primary',
                  attrs: { type: 'button' },
                  html: `${icon('gallery', 15)}<span>Keep</span>`,
                  title: 'File this render in Shots',
                  on: {
                    click: (event) => {
                      const button = event.currentTarget as HTMLButtonElement;
                      button.disabled = true;
                      const span = button.querySelector('span');
                      if (span) span.textContent = 'Kept';
                      void this.file(capture, [image], preset);
                    },
                  },
                }),
            // The anchor's own `download` saves the render; the handler adds the model view beside
            // it. Left as a real link rather than a button so right-click → Save As still works on
            // the image the user actually came for.
            el('a', {
              class: 'lg-btn',
              attrs: { href: afterUrl, download: renderName },
              title: `Saves two files — ${renderName} and ${modelName}`,
              html: `${icon('download', 15)}<span>Download</span>`,
              on: { click: () => this.saveAlongside(beforeUrl, modelName) },
            }),
            el('button', {
              class: 'lg-btn lg-btn--ghost',
              attrs: { type: 'button' },
              text: 'Render again',
              title: 'Add a note or reference photos, then recapture this view and render it again',
              on: { click: () => this.openAsk() },
            }),
          ),
          el(
            'details',
            { class: 'lg-quick__prompt' },
            el('summary', { text: 'Prompt sent with the capture' }),
            el('pre', { text: this.lastPrompt }),
          ),
        ),
      );
    });
  }

  /**
   * Save the model view under the render that was just downloaded.
   *
   * The pair is the point: the AI image is only defensible next to the geometry it was made from, and
   * a designer filing one without the other has to go back and re-export. Fired on a short timer
   * rather than in the same tick because two saves in one gesture read to Chromium as a
   * multiple-download attempt — separated, they are two ordinary saves, and the button's title says
   * up front that two files are coming.
   */
  private saveAlongside(url: string, filename: string): void {
    globalThis.setTimeout(() => {
      const link = el('a', { attrs: { href: url, download: filename } });
      document.body.appendChild(link);
      link.click();
      link.remove();
    }, 300);
  }

  /**
   * File a render in Shots: the capture itself once, then each render against it.
   *
   * The shot is written on the first keep and reused after that, so keeping two variations of one
   * render produces one entry with two variants rather than two near-identical shots.
   */
  private async file(capture: CaptureResult, images: Blob[], preset: StylePreset): Promise<void> {
    const store = this.deps.store?.() ?? null;
    if (store === null) return;

    try {
      let shotId = this.filedShotId;
      let shot: StoredShot | undefined;
      if (shotId === null) {
        this.shotCounter += 1;
        shot = await store.save({
          title: `Quick render ${this.shotCounter} · ${stamp()}`,
          size: capture.size,
          beauty: capture.beauty,
          idMap: capture.idMap,
          depth: capture.depth,
          thumbnail: capture.thumbnail,
          manifest: capture.manifest,
          camera: capture.camera,
          environment: capture.environment,
        });
        shotId = shot.id;
        this.filedShotId = shotId;
      }

      for (const image of images) {
        shot =
          (await store.addVariant(shotId, {
            presetId: preset.id,
            presetLabel: preset.label,
            prompt: this.lastPrompt,
            model: ENHANCE_MODEL,
            quality: this.settings.quality,
            size: capture.size,
            image,
            estimatedCostUsd: estimateCostUsd(capture.size, this.settings.quality, 1),
          })) ?? shot;
      }
      if (shot) this.deps.onFiled?.(shot);
    } catch (error) {
      // Filing is a convenience on top of a render the user already has on screen and can download,
      // so a storage failure reports itself and leaves the result standing.
      this.setStatus(
        error instanceof Error ? `Could not file this render: ${error.message}` : 'Could not file this render.',
        'error',
      );
    }
  }

  private openSheet(): void {
    this.sheet.hidden = false;
  }

  private closeSheet(): void {
    if (this.running) this.controller?.abort();
    this.closeAsk();
    this.sheet.hidden = true;
    this.clearSheet();
  }

  private clearSheet(): void {
    this.stopWorking();
    clear(this.sheetBody);
    this.filedShotId = null;
    this.revokeUrls();
  }

  private trackUrl(url: string): string {
    this.objectUrls.push(url);
    return url;
  }

  private revokeUrls(): void {
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls.length = 0;
  }

  dispose(): void {
    this.controller?.abort();
    this.stopWorking();
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.revokeUrls();
    // Reference previews outlive the sheet by design, so they are not in `objectUrls` and have to be
    // released here or a session of uploads leaks every one of them.
    for (const entry of this.references) URL.revokeObjectURL(entry.url);
    this.references.length = 0;
    this.root.remove();
  }
}

function field(label: string, ...children: (Node | string)[]): HTMLElement {
  return el(
    'div',
    { class: 'lg-quick__field' },
    el('span', { class: 'lg-quick__fieldlabel', text: label }),
    ...children,
  );
}

/** Local clock time, so two renders of the same view are told apart at a glance in the gallery. */
function stamp(): string {
  return new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The same instant as `stamp`, safe to put in a filename: `YYYYMMDD-HHMMSS`.
 *
 * Locale time carries a colon, which Windows rejects outright and which the download attribute
 * silently mangles elsewhere — so the saved file is stamped in fixed digits rather than in prose.
 * The date is here and not only the clock time because these files accumulate in a downloads folder
 * across weeks, where two renders made at 14:22 on different days are a collision waiting to happen.
 */
function fileStamp(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `${date}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}
